import * as Sentry from '@sentry/react';
import React, { useEffect, useRef } from 'react';
import { Route as RouteV5, Switch as SwitchV5 } from 'react-router-dom';
import {
  Navigate,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom-v5-compat';

import { OrgSwitcher } from 'src/components/appNav/orgSwitcher/OrgSwitcher';
import { LoggedInRoute } from 'src/components/common/authenticatedRoutes/AuthenticatedRoutes';
import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { SupportRequest } from 'src/components/support-request/SupportRequest';
import { Toasts } from 'src/components/toast/Toast';
import { useAuth } from 'src/context/AuthContext';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useInitializeClickAnalytics } from 'src/hooks/useInitializeClickAnalytics';
import {
  SentryRoutes as Routes,
  useTelemetryInitializer,
} from 'src/hooks/useTelemetryInitializer';
import { registerCallbackForGuideOrSurvey } from 'src/lib/analytics/amplitude/helpers/registerCallbackForGuideOrSurvey';
import { useSyncAmplitudeForEmbedded } from 'src/lib/analytics/amplitude/hooks/useSyncAmplitudeForEmbedded';
import Config from 'src/lib/config';
import { useDatadogTrackingConsent } from 'src/lib/consent/useDatadogTrackingConsent';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { listenForKonami } from 'src/templates/index/konami';

import { AccountInvitationHandler } from '../account/accountInvitationHandler/AccountInvitationHandler';
import { EmbedLogoutPage } from '../account/logoutPage/EmbedLogoutPage';
import { LogoutPage } from '../account/logoutPage/LogoutPage';
import { invite, org, userSettingsRouteConfig } from '../account/routes';
import { AppLayout } from '../appLayout/AppLayout';
import { GlobalHeader } from '../appLayout/components/globalHeader/GlobalHeader';
import { BuildInfoPage } from '../buildInfo/buildInfoPage/BuildInfoPage';
import { EmbedAuthenticator } from '../embeddableExplorer/EmbedAuthenticator';
import { EmbeddableExplorer } from '../embeddableExplorer/EmbeddableExplorer';
import { isEmbeddableExplorerRoute } from '../embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from '../embeddableSandbox/isEmbeddableSandboxRoute';
import { PreflightScriptOAuthTokenFetcher } from '../graph/explorerPage/explorerSettings/PreflightScriptOAuthConfirmationModal';
import {
  graphPersistedQueriesRouteConfig,
  graphProposalListRouteConfig,
  proposalRouteConfig,
  sandboxGraphRouteConfig,
  variantRouteConfig,
} from '../graph/routes';
import {
  Onboarding,
  ownedRoutes as onboardingOwnedRoutes,
} from '../onboarding/Onboarding';
import * as routes from '../onboarding/routes';
import { TokenValidatePage } from '../onboarding/views/tokenValidationPage/TokenValidationPage';
import * as sharedRoutes from '../shared/routes';

import { loggedInRedirects } from './loggedInRedirects/loggedInRedirects';
import { loggedOutRedirects } from './loggedOutRedirects/loggedOutRedirects';
import { LoginWithProvider } from './LoginWithProvider';
import { BuildRoute, NotFoundRoute, RootRoute } from './routes';
import { ServiceWorkerRefreshPrompts } from './ServiceWorkerRefreshPrompts';

const EmbeddableSandbox = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "EmbeddableSandbox" */ '../embeddableSandbox/EmbeddableSandbox'
    )
  ).EmbeddableSandbox,
}));
const UserProfileSurvey = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "UserProfileSurvey" */ '../onboarding/views/userProfileSurvey/UserProfileSurvey'
    )
  ).UserProfileSurvey,
}));
const AccountLayout = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "AccountLayout" */ '../account/accountLayout/AccountLayout'
    )
  ).AccountLayout,
}));
const SettingsPageLayout = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "SettingsPageLayout" */ '../user/settingsPage/SettingsPageLayout'
    )
  ).SettingsPageLayout,
}));
const VariantLayout = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "VariantLayout" */ '../graph/variantLayout/VariantLayout'
    )
  ).VariantLayout,
}));
const SandboxLayout = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "SandboxLayout" */ '../graph/variantLayout/SandboxLayout'
    )
  ).SandboxLayout,
}));
const ProposalLayout = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "ProposalLayout" */ '../graph/proposalLayout/ProposalLayout'
    )
  ).ProposalLayout,
}));
const GraphLayout = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "GraphLayout" */ '../graph/graphLayout/GraphLayout'
    )
  ).GraphLayout,
}));

const SelectPlanPageWrapper = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "SelectPlanPageWrapper" */ '../onboarding/views/selectPlanPage/SelectPlanPageWrapper'
    )
  ).SelectPlanPageWrapper,
}));

const SelectPlanCheckoutPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "SelectPlanCheckoutPage" */ '../onboarding/views/selectPlanPage/SelectPlanCheckoutPage'
    )
  ).SelectPlanCheckoutPage,
}));

const ResolvePlanIssuesPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "ResolvePlanIssuesPage" */ '../onboarding/views/selectPlanPage/ResolvePlanIssuesPage'
    )
  ).ResolvePlanIssuesPage,
}));

/*
 * Studio – the app!
 *
 * Authentication is enforced on LoggedInRoute's, Route's are public,
 * but will need to be placed above
 * <LoggedInRoute path={RootRoute.definition} component={AppLayout} />
 * since it matches everything
 *
 * If you visit a protect route while logged out, you'll be forwarded to /login.
 * If you visited /login while logged in, you'll be sent to the root url /.
 */
export const RoutedApp = Sentry.withProfiler(() => {
  const location = useLocation();
  const [_, setCurrentAccountId] = useCurrentAccountId();
  const { ssoLoginUrl, account, accountId, loading, isSudo } = useAuth();
  const hasRedirectedRef = useRef(false);

  const navigate = useNavigate();
  React.useEffect(() => {
    window?.engagement?.setRouter((newUrl) => navigate(newUrl));
  }, [navigate]);

  useEffect(() => {
    // workaround for amplitude requiring a callback
    registerCallbackForGuideOrSurvey('CHECKLIST_ITEM_COMPLETED', () => {});

    document.addEventListener('keydown', listenForKonami);
    return () => document.removeEventListener('keydown', listenForKonami);
  }, []);

  useInitializeClickAnalytics();

  const isEmbeddableExplorer = isEmbeddableExplorerRoute();
  const isEmbeddableSandbox = isEmbeddableSandboxRoute();

  useSyncAmplitudeForEmbedded();
  useTelemetryInitializer(isEmbeddableExplorer || isEmbeddableSandbox);
  useDatadogTrackingConsent();

  const graphRefMatch = location.pathname.match(
    /\/graph\/(([^@\/]+)@([^@\/]+))/,
  );
  if (isMinLengthArray(4, graphRefMatch)) {
    const [, graphRef, graphId, variant] = graphRefMatch;
    return (
      <Navigate
        replace={true}
        to={{
          ...location,
          pathname: location.pathname.replace(
            graphRef,
            `${graphId}/variant/${variant}`,
          ),
        }}
      />
    );
  }

  const shouldRedirect = accountId !== account?.id;

  if (
    !loading &&
    !isSudo &&
    shouldRedirect &&
    ssoLoginUrl &&
    ssoLoginUrl.startsWith('https://') &&
    !isEmbeddableSandbox &&
    !isEmbeddableExplorer &&
    !hasRedirectedRef.current
  ) {
    // We need to track if we've already redirected to the SSO login url
    // because we don't want to redirect again if the user is already being
    // redirected to the SSO login url. This is to prevent infinite redirects.
    hasRedirectedRef.current = true;
    setCurrentAccountId(accountId);
    window.location.href = ssoLoginUrl;
    return <Loading />;
  }

  if (loading || hasRedirectedRef.current) return <Loading />;

  return (
    <ErrorBoundary>
      <ServiceWorkerRefreshPrompts />
      <LazyUrlModal name={Config.modals.supportRequest}>
        {({ isOpen, closeModal }) => (
          <SupportRequest isOpen={isOpen} closeModal={closeModal} />
        )}
      </LazyUrlModal>
      <React.Suspense fallback={<Loading />}>
        {isEmbeddableExplorer ? (
          <Routes>
            <Route
              path={routes.embedLogoutRouteConfig.splatDefinition}
              Component={EmbedLogoutPage}
            />
            <Route
              path={sharedRoutes.preflightOAuth2RedirectRoute.splatDefinition}
              Component={PreflightScriptOAuthTokenFetcher}
            />
            <Route
              path={RootRoute.splatDefinition}
              element={<EmbeddableExplorer />}
            />
          </Routes>
        ) : isEmbeddableSandbox ? (
          <Routes>
            <Route
              path={routes.embedLogoutRouteConfig.splatDefinition}
              Component={EmbedLogoutPage}
            />
            <Route
              path={sharedRoutes.preflightOAuth2RedirectRoute.splatDefinition}
              Component={PreflightScriptOAuthTokenFetcher}
            />
            <Route
              path={sandboxGraphRouteConfig.splatDefinition}
              Component={EmbeddableSandbox}
            />
          </Routes>
        ) : (
          <Routes>
            {loggedOutRedirects(location)}
            <Route
              path={sharedRoutes.preflightOAuth2RedirectRoute.splatDefinition}
              Component={PreflightScriptOAuthTokenFetcher}
            />
            <Route
              path={routes.logoutRouteConfig.splatDefinition}
              Component={LogoutPage}
            />
            <Route path={NotFoundRoute.splatDefinition} Component={NotFound} />
            <Route
              path={routes.loginWithProvider.definition}
              Component={LoginWithProvider}
            />
            {onboardingOwnedRoutes.map((definition) => (
              <Route
                key={definition}
                path={definition}
                Component={Onboarding}
              />
            ))}
            <Route
              path={routes.userProfileSurvey.splatDefinition}
              element={<LoggedInRoute Component={UserProfileSurvey} />}
            />
            <Route
              path={BuildRoute.splatDefinition}
              Component={BuildInfoPage}
            />
            <Route
              path={routes.embedAuthenticationRouteConfig.splatDefinition}
              element={
                <>
                  <EmbedAuthenticator />
                  <Toasts />
                </>
              }
            />
            <Route
              path={routes.selectPlan.definition}
              Component={SelectPlanPageWrapper}
            />
            <Route
              path={routes.resolve.definition}
              Component={ResolvePlanIssuesPage}
            />
            <Route
              path={routes.checkout.definition}
              Component={SelectPlanCheckoutPage}
            />
            <Route
              path={routes.tokenValidate.definition}
              Component={TokenValidatePage}
            />
            <Route
              element={
                [
                  // List of app routes that do not require the user to be logged in
                  sandboxGraphRouteConfig.definition,
                  '/public',
                ].some((d) => location.pathname.startsWith(d)) ? (
                  <AppLayout />
                ) : (
                  <LoggedInRoute Component={AppLayout} />
                )
              }
            >
              {loggedInRedirects()}
              <Route
                path={invite.splatDefinition}
                Component={AccountInvitationHandler}
              />
              {variantRouteConfig.patchV5DefinitionsForV6(false).map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ErrorBoundary>
                      <React.Suspense fallback={<Loading />}>
                        <VariantLayout />
                      </React.Suspense>
                    </ErrorBoundary>
                  }
                />
              ))}

              <Route
                path={sandboxGraphRouteConfig.splatDefinition}
                element={
                  <ErrorBoundary>
                    <React.Suspense fallback={<Loading />}>
                      <SandboxLayout />
                    </React.Suspense>
                  </ErrorBoundary>
                }
              />
              {proposalRouteConfig
                .patchV5DefinitionsForV6(false)
                .map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ErrorBoundary>
                        <React.Suspense fallback={<Loading />}>
                          <ProposalLayout />
                        </React.Suspense>
                      </ErrorBoundary>
                    }
                  />
                ))}
              {[
                ...graphPersistedQueriesRouteConfig.patchV5DefinitionsForV6(
                  false,
                ),
                ...graphProposalListRouteConfig.patchV5DefinitionsForV6(false),
              ].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ErrorBoundary>
                      <React.Suspense fallback={<Loading />}>
                        {/* Placeholder for new Graph IA layout */}
                        <GraphLayout />
                      </React.Suspense>
                    </ErrorBoundary>
                  }
                />
              ))}
              {userSettingsRouteConfig
                .patchV5DefinitionsForV6(false)
                .map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
                        <GlobalHeader leftNavContext="collapsed">
                          <OrgSwitcher collapseOrgName={false} />
                        </GlobalHeader>
                        <div className="flex h-full flex-1 overflow-y-auto">
                          <Toasts />
                          <ErrorBoundary>
                            <React.Suspense fallback={<Loading />}>
                              <SwitchV5>
                                <RouteV5 component={SettingsPageLayout} />
                              </SwitchV5>
                            </React.Suspense>
                          </ErrorBoundary>
                        </div>
                      </div>
                    }
                  />
                ))}
              <Route
                path={org.splatDefinition}
                element={
                  <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
                    <SwitchV5>
                      <RouteV5 path={[invite.definition]} />

                      {/* Routes listed above are those for which the nav should be hidden */}
                      <RouteV5
                        render={() => (
                          <GlobalHeader leftNavContext="collapsed">
                            <OrgSwitcher collapseOrgName={false} />
                          </GlobalHeader>
                        )}
                      />
                    </SwitchV5>
                    <div className="flex h-full flex-1 overflow-y-auto">
                      <Toasts />
                      <ErrorBoundary>
                        <React.Suspense fallback={<Loading />}>
                          <AccountLayout />
                        </React.Suspense>
                      </ErrorBoundary>
                    </div>
                  </div>
                }
              />
              <Route
                path="*"
                element={
                  <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
                    <GlobalHeader leftNavContext="collapsed" />
                    <div className="flex h-full flex-1 overflow-y-auto">
                      <NotFound />
                    </div>
                  </div>
                }
              />
            </Route>
          </Routes>
        )}
      </React.Suspense>
    </ErrorBoundary>
  );
});
